import {
    Box,
    Button,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Chip,
    Theme,
} from '@mui/material';
import { BaseSelectField } from 'common/components/base-select-field';
import FormProvider from 'common/components/form-provider';
import React, { useEffect } from 'react';
import { FieldError, useForm } from 'react-hook-form';
import QuestionCheckbox from './question/question-checkbox';
import { yupResolver } from '@hookform/resolvers/yup';
import { campaignDesignQuestionSchema } from 'common/schema';
import { colors, styleSelect } from 'common/constants';
import QuestionScoreStar from './question/question-score-star';
import {
    IQuestionNewEditFormProps,
    IQuestionNewEditForm,
    ISkipQuestionLogicDisplay,
} from 'types/campaign-design';
import QuestionScore from './question/question-score';
import QuestionDropdown from './question/question-dropdown';
import QuestionImage from './question/question-image';
import QuestionImageWithTitle from './question/question-image-with-title';
import QuestionTextRecommend from './question/question-textbox-recommend';
import QuestionEmoji from './question/question-emoji';
import QuestionTextboxRecommendMultiChoice from './question/question-textbox-recommend-multi-choice';
import QuestionRank from './question/question-rank';
import QuestionSliderScale from './question/question-slider-scale';
import QuestionMatrixText from './question/question-matrix-text';
import QuestionMatrixCheckbox from './question/question-matrix-checkbox';
import QuestionMatrixRadio from './question/question-matrix-radio';
import QuestionSingleChoice from './question/question-single-choice';
import { common } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { createStyleSheet } from 'utils/helpers';
import ModalQuestionLogic from './setting-logic/modal-logic';
import BaseDialog from 'common/components/base-dialog';
import { questionTypeOptions } from 'configs/question-type';
import QuestionNoContent from './question/question-no-content';
import {
    LogicQuestionType,
    LOGIC_AVAILABLE_BY_QUESTION_TYPE,
    QuestionLogic,
    LOGIC_MENU_ITEM_TEXT,
} from 'pages/admin/campaign-overview/constants';
import CloseIcon from '@mui/icons-material/Close';
import {
    deleteQuestionSkipLogic,
    deleteQuestionCreeningLogic,
    questionTitleLogicOptions,
    deleteQuestionTitleLogic,
    deleteAnswerFromOtherQuestionLogic,
    deleteQuestionBranchLogic,
    // updateCampaignQuestion,
    // createCampaignQuestion,
} from 'services/campaign.service';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    listCampaignQuestionsFormatter,
    checkMentionAndFormatQuestion,
    logicDisplayAnswerOnTitlteTransformMarkUp,
    resetValuesWhenChangeQuestionType,
} from 'utils/campaign_helpers';
import BaseTextMetionsField from 'common/components/base-text-metions';
import { IQuestionLogicDisplayAnswerData } from 'types/campaign';
import { QuestionType as EnumQuestionType } from 'pages/admin/campaign-overview/constants';
import { useAppSelector } from 'common/hook';

const QuestionNewEditForm: React.FC<IQuestionNewEditFormProps> = ({
    selectedId,
    question,
    index,
    handleItemSelect,
    methodsRef,
    onDragStart,
    onDragOver,
    onDrop,
    handleDeleteQuestion,
    handleMarkQuestionAsRequired,
    handleCopyQuestion,
    replaceQuestionByQuestionId,
    hasTwoOrMoreQuestions,
}) => {
    const defaultTrigger = '$title';
    const randomTrigger = () => {
        return `$${Math.random().toString(36).substring(7)}`;
    };
    const [mentionTrigger, setMentionTrigger] = React.useState(
        question.questionIdForTitle || !question.id
            ? randomTrigger()
            : defaultTrigger
    );
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const [isDeleteLogicDialogOpen, setIsDeleteLogicDialogOpen] =
        React.useState(false);
    const [displayAnswerData, setDisplayAnswerData] = React.useState<
        IQuestionLogicDisplayAnswerData[]
    >([]);

    const currentCampaignId = Number(useParams().id);
    const currentCampaign = useAppSelector(
        (state) => state.campaignState.campaign
    );

    const [currentLogicToDelete, setCurrentLogicToDelete] =
        React.useState<string>('');

    const questionData = (question: IQuestionNewEditForm) => {
        return {
            id: question.id,
            question: checkMentionAndFormatQuestion(question),
            questionType: question.questionType,
            data: question.data,
            questionNumber: question.questionNumber,
            questionIdForTitle: question.questionIdForTitle,
            mentionStartPos: question.mentionStartPos,
            mentionEndPos: question.mentionEndPos,
            mentionValue: `\${${question.questionTitleLogic?.question}}`,
            takeAnswerFromOtherQuestion:
                !!question.answerFromOtherQuestionLogic?.id,
        };
    };

    const methods = useForm({
        resolver: yupResolver(campaignDesignQuestionSchema),
        defaultValues: {
            ...questionData(question),
            id: question.id ? String(question.id) : null,
        },
        mode: 'onChange',
    });
    const {
        handleSubmit,
        watch,
        reset,
        register,
        // trigger,
        formState: { isDirty },
    } = methods;
    const questionType = watch('questionType');
    const watchQuestion = watch('question');
    const mentionValue = watch('mentionValue') || '';
    // const id = watch('id');

    methodsRef.current[index] = methods;

    const onSubmit = handleSubmit((data) => {
        console.log('submit inside form', data);
    });

    // DO NOT REMOVE THIS useEffect and the console.log inside it
    // This useEffect is mandatory to handle the dirty state of the form
    useEffect(() => {
        if (isDirty) {
            console.log(`${index} isDirty`);
        } else {
            console.log(`${index} isClean`);
        }
    }, [isDirty, index]);

    const [openModalLogic, setOpenModalLogic] = React.useState(false);

    useEffect(() => {
        // check if mentionValue not in watchQuestion
        if (
            watchQuestion &&
            watchQuestion.indexOf(mentionValue.toString()) === -1
        ) {
            reset(
                (prev) => ({
                    ...prev,
                    questionIdForTitle: 0,
                    mentionStartPos: null,
                    mentionEndPos: null,
                    mentionValue: '',
                }),
                { keepDirty: true }
            );
            setMentionTrigger(defaultTrigger);
        }
    }, [watchQuestion, mentionValue, reset]);

    // handle when change question type
    useEffect(() => {
        if (questionType && question) {
            const resetParams = resetValuesWhenChangeQuestionType(
                {
                    ...question,
                    question: watchQuestion,
                },
                questionType
            );
            reset(
                (prev) => ({
                    ...prev,
                    ...resetParams,
                }),
                {
                    keepDirty: true,
                }
            );
        }
        // eslint-disable-next-line
    }, [questionType, question, reset]);

    // handle when open question
    useEffect(() => {
        const fetchDataWhenSelectQuestion = async () => {
            const displayAnswerOptions = await questionTitleLogicOptions(
                currentCampaignId,
                Number(question.id)
            );
            setDisplayAnswerData(displayAnswerOptions.data.logics || []);
        };
        if (selectedId === index) {
            reset((prev) => ({
                ...prev,
                ...questionData(question),
            }));
            fetchDataWhenSelectQuestion();
        }
    }, [selectedId, reset, index, question, currentCampaignId]);
    const renderQuestionType = () => {
        const newProps = {
            question: question,
            onDragOver: onDragOver,
            onDrop: onDrop,
            onDragStart: onDragStart,
            replaceQuestionByQuestionId,
        };
        switch (questionType) {
            case EnumQuestionType.Textbox:
            case EnumQuestionType.Date:
            case EnumQuestionType.Number:
                return <QuestionNoContent {...newProps} />;
            case EnumQuestionType.Checkbox:
                return <QuestionCheckbox {...newProps} />;
            case EnumQuestionType.SingleChoice:
                return <QuestionSingleChoice {...newProps} />;
            case EnumQuestionType.ScoreStarFull:
                return <QuestionScoreStar {...newProps} starScoreFull={true} />;
            case EnumQuestionType.ScoreStarHalf:
                return (
                    <QuestionScoreStar {...newProps} starScoreFull={false} />
                );
            case EnumQuestionType.Score:
                return <QuestionScore {...newProps} />;
            case EnumQuestionType.Dropdown:
                return <QuestionDropdown {...newProps} />;
            case EnumQuestionType.Image:
                return <QuestionImage {...newProps} />;
            case EnumQuestionType.ImageWithTitle:
                return <QuestionImageWithTitle {...newProps} />;
            case EnumQuestionType.TextboxRecommend:
                return <QuestionTextRecommend {...newProps} />;
            case EnumQuestionType.Emoji:
                return <QuestionEmoji {...newProps} />;
            case EnumQuestionType.TextboxRecommendMultiChoice:
                return <QuestionTextboxRecommendMultiChoice {...newProps} />;
            case EnumQuestionType.Rank:
                return <QuestionRank {...newProps} />;
            case EnumQuestionType.SliderScale:
                return <QuestionSliderScale {...newProps} />;
            case EnumQuestionType.MatrixText:
                return <QuestionMatrixText {...newProps} />;
            case EnumQuestionType.MatrixCheckbox:
                return <QuestionMatrixCheckbox {...newProps} />;
            case EnumQuestionType.MatrixRadio:
                return <QuestionMatrixRadio {...newProps} />;
            default:
                return null;
        }
    };

    const renderLogicMenuItems = () => {
        if (selectedId === index) {
            const qtype =
                questionType as keyof typeof LOGIC_AVAILABLE_BY_QUESTION_TYPE;
            const QuestionLogic: QuestionLogic[] =
                LOGIC_AVAILABLE_BY_QUESTION_TYPE[qtype];
            // if (!question.id) {
            //     // remove LogicQuestionType.Branch from QuestionLogic
            //     const index = QuestionLogic.indexOf(LogicQuestionType.Branch);
            //     if (index > -1) {
            //         QuestionLogic.splice(index, 1);
            //     }
            // }
            return QuestionLogic.map((type) => {
                return (
                    <MenuItem
                        key={type}
                        sx={styles.menuLogic}
                        disableRipple={true}
                        onClick={() => {
                            setLogicType(type);
                            setOpenModalLogic(true);
                            handleClosePopoverLogicType();
                        }}
                    >
                        <span>{LOGIC_MENU_ITEM_TEXT[type].text_en}</span>
                        <span>{LOGIC_MENU_ITEM_TEXT[type].text_vi}</span>
                    </MenuItem>
                );
            });
        }
    };

    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
        null
    );

    const handleSelectLogicType = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        if (!question.id || isDirty) {
            toast.error('Hãy lưu câu hỏi trước khi thêm logic');
            return;
        }
        onSubmit();
        setAnchorEl(event.currentTarget);
        setTimeout(() => {
            if (!anchorEl) return;
            anchorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
    };

    const handleClosePopoverLogicType = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    const onConfirmDeleteQuestion = () => {
        setIsDeleteDialogOpen(false);
        handleDeleteQuestion && handleDeleteQuestion();
    };

    const onFlipQuestionRequired = () => {
        if (!question.id || isDirty) {
            toast.error('Hãy lưu câu hỏi trước khi đánh dấu bắt buộc');
            return;
        }
        handleMarkQuestionAsRequired && handleMarkQuestionAsRequired();
    };

    const prepareSkipLogicText = (
        skipLogic: ISkipQuestionLogicDisplay,
        logicIndex: number
    ) => {
        const conditionText = skipLogic.condition === 1 ? 'chọn' : 'không chọn';
        if (logicIndex === 0) {
            return (
                <span>
                    Bỏ qua câu này, nếu{' '}
                    <b>
                        &quot;Q{skipLogic.number}. {skipLogic.question}&quot;
                    </b>{' '}
                    {conditionText} <b>&quot;{skipLogic.answer}&quot;</b>.
                </span>
            );
        } else {
            return (
                <span>
                    Và nếu{' '}
                    <b>
                        &quot;Q{skipLogic.number}. {skipLogic.question}&quot;
                    </b>{' '}
                    {conditionText} <b>&quot;{skipLogic.answer}&quot;</b>.
                </span>
            );
        }
    };

    const prepareCreeningLogicText = (
        logic: ISkipQuestionLogicDisplay,
        logicIndex: number
    ) => {
        const conditionText = logic.condition === 1 ? 'chọn' : 'không chọn';
        if (logicIndex === 0) {
            return (
                <span>
                    Kết thúc khảo sát này, nếu{' '}
                    <b>
                        &quot;Q{logic.number}. {logic.question}&quot;
                    </b>{' '}
                    {conditionText} <b>&quot;{logic.answer}&quot;</b>.
                </span>
            );
        } else {
            return (
                <span>
                    Và, nếu{' '}
                    <b>
                        &quot;Q{logic.number}. {logic.question}&quot;
                    </b>{' '}
                    {conditionText} <b>&quot;{logic.answer}&quot;</b>.
                </span>
            );
        }
    };

    const prepareQuestionTitleLogicText = (
        logic: IQuestionNewEditForm['questionTitleLogic']
    ) => {
        return (
            <span>
                Lấy đáp án đã được lựa chọn ở câu hỏi trước{' '}
                <b>
                    &quot;Q{logic?.number}. {logic?.question}&quot;
                </b>{' '}
                điền vào title của câu hỏi này.
            </span>
        );
    };

    const prepareAnswerFromOtherQuestionLogicText = (
        questionType: string,
        logic: IQuestionNewEditForm['answerFromOtherQuestionLogic']
    ) => {
        if (
            questionType === EnumQuestionType.MatrixCheckbox ||
            questionType === EnumQuestionType.MatrixRadio ||
            questionType === EnumQuestionType.MatrixText
        ) {
            return (
                <span>
                    {logic?.row_question_number && (
                        <>
                            Đáp án ở hàng sẽ được lấy từ các đáp án đã được lựa
                            chọn ở câu hỏi{' '}
                            <b>
                                &quot;Q{logic?.row_question_number}.{' '}
                                {logic?.row_question}
                                &quot;
                            </b>
                        </>
                    )}
                    {logic?.column_question_number && (
                        <>
                            <br />
                            Đáp án ở cột sẽ được lấy từ các đáp án đã được lựa
                            chọn ở câu hỏi{' '}
                            <b>
                                &quot;Q{logic?.column_question_number}.{' '}
                                {logic?.column_question}&quot;
                            </b>
                        </>
                    )}
                </span>
            );
        }
        return (
            <span>
                Đáp án ở câu hỏi này sẽ được lấy từ các đáp án đã được lựa chọn
                ở câu hỏi{' '}
                <b>
                    &quot;Q{logic?.number}. {logic?.question}&quot;
                </b>
            </span>
        );
    };

    const prepareBranchLogicText = (
        logic: ISkipQuestionLogicDisplay,
        logicIndex: number
    ) => {
        const conditionText = logic.condition === 1 ? 'chọn' : 'không chọn';
        if (logicIndex === 0) {
            return (
                <span>
                    Khảo sát rẽ nhánh, nếu {conditionText} đáp án{' '}
                    <b>&quot;{logic.answer}&quot;</b> này thì đi đến câu{' '}
                    <b>
                        &quot;Q{logic?.number}. {logic?.question}&quot;
                    </b>
                    .
                </span>
            );
        } else {
            return (
                <span>
                    Và, nếu {conditionText} đáp án{' '}
                    <b>&quot;{logic.answer}&quot;</b> này thì đi đến câu{' '}
                    <b>
                        &quot;Q{logic?.number}. {logic?.question}&quot;
                    </b>
                    .
                </span>
            );
        }
    };

    const onConfirmDeleteQuestionLogic = async () => {
        let response;
        if (currentLogicToDelete === LogicQuestionType.Skip) {
            response = await deleteQuestionSkipLogic(
                currentCampaignId,
                Number(question.id)
            );
        } else if (currentLogicToDelete === LogicQuestionType.Creening) {
            response = await deleteQuestionCreeningLogic(
                currentCampaignId,
                Number(question.id)
            );
        } else if (currentLogicToDelete === LogicQuestionType.DisplayOnTitle) {
            response = await deleteQuestionTitleLogic(
                currentCampaignId,
                Number(question.id)
            );
        } else if (
            currentLogicToDelete === LogicQuestionType.OnlyDisplaySelectedChoice
        ) {
            response = await deleteAnswerFromOtherQuestionLogic(
                currentCampaignId,
                Number(question.id)
            );
        } else if (currentLogicToDelete === LogicQuestionType.Branch) {
            response = await deleteQuestionBranchLogic(
                currentCampaignId,
                Number(question.id)
            );
        } else {
            return;
        }
        if (!response) return;
        if (response.success) {
            toast.success(response.message);
            replaceQuestionByQuestionId(
                Number(question.id),
                listCampaignQuestionsFormatter(response.data.question)
            );
        } else {
            toast.error(response.message);
        }
        setIsDeleteLogicDialogOpen(false);
    };

    const onSetDeleteQuestionLogicSkip = () => {
        setCurrentLogicToDelete(LogicQuestionType.Skip);
        setIsDeleteLogicDialogOpen(true);
    };

    const onSetDeleteCreeningLogic = () => {
        setCurrentLogicToDelete(LogicQuestionType.Creening);
        setIsDeleteLogicDialogOpen(true);
    };

    const onSetDeleteQuestionTitleLogic = () => {
        setCurrentLogicToDelete(LogicQuestionType.DisplayOnTitle);
        setIsDeleteLogicDialogOpen(true);
    };

    const onSetDeleteAnswerFromOtherQuestionLogic = () => {
        setCurrentLogicToDelete(LogicQuestionType.OnlyDisplaySelectedChoice);
        setIsDeleteLogicDialogOpen(true);
    };

    const onSetDeleteBranchLogic = () => {
        setCurrentLogicToDelete(LogicQuestionType.Branch);
        setIsDeleteLogicDialogOpen(true);
    };

    const customMentionStyle = (error: FieldError | undefined) => {
        const fontSize = 20;
        return {
            control: {
                fontSize,
                fontWeight: '400',
                minHeight: '65px',
                width: '100%',
                border: error
                    ? `1px solid ${colors.textError}`
                    : '1px solid #ccc',
                borderRadius: '50px',
                outline: 'none',
                backgroundColor: 'white',
                padding: '8px 24px',
            },
            input: {
                fontSize,
                fontWeight: '400',
                minHeight: '65px',
                width: '100%',
                border: error
                    ? `1px solid ${colors.textError}`
                    : '1px solid #ccc',
                borderRadius: '50px',
                outline: 'none',
                backgroundColor: 'white',
                padding: '18px 24px',
            },
            suggestions: {
                marginLeft: '40px',
                list: {
                    backgroundColor: 'white',
                    border: '1px solid #ddd',
                    fontSize,
                },
                item: {
                    padding: '8px 16px',
                    borderBottom: '1px solid #ddd',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'black',
                    },
                },
            },
        };
    };

    const [logicType, setLogicType] = React.useState<QuestionLogic>(
        LogicQuestionType.Skip
    );

    const isCampaignPublicWithCode =
        currentCampaign?.kind === 'public_with_code';

    return (
        <>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems={'flex-start'}
                    gap={2}
                    marginBottom={2}
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                        handleItemSelect && handleItemSelect(index);
                    }}
                    id={`question-${index}`}
                >
                    <Stack
                        flex={1}
                        sx={{
                            background: `${index === selectedId ? 'var(--color-primary-light-20)' : common.white}`,
                            borderRadius: '22px',
                            border: `1px solid ${index === selectedId ? 'var(--color-primary)' : '#0000004D'}`,
                        }}
                    >
                        {selectedId !== index ? (
                            <>
                                <Stack
                                    direction={'row'}
                                    alignItems={'center'}
                                    gap={1}
                                    justifyContent={'space-between'}
                                    sx={{
                                        padding: '35px',
                                        borderTopLeftRadius: '22px',
                                        borderTopRightRadius: '22px',
                                    }}
                                    draggable={false}
                                    onDragStart={(event) =>
                                        onDragStart &&
                                        onDragStart(event, question)
                                    }
                                    onDragOver={(event) =>
                                        onDragOver &&
                                        onDragOver(event, question)
                                    }
                                    onDrop={(event) =>
                                        onDrop && onDrop(event, question)
                                    }
                                >
                                    <Box
                                        display={'flex'}
                                        alignItems={'center'}
                                        gap={3}
                                    >
                                        <Box
                                            sx={{
                                                padding: '8px',
                                                textAlign: 'center',
                                                width: '65px',
                                                height: '65px',
                                                fontSize: '28px',
                                                borderRadius: '10px',
                                                background: '#EFEFEF',
                                                border: `1px solid ${index === selectedId ? colors.main : '#8B8B8B'}`,
                                                color:
                                                    index === selectedId
                                                        ? colors.main
                                                        : common.black,
                                                fontWeight: '600',
                                                display: 'flex',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                position: 'relative',
                                            }}
                                        >
                                            <Box>{`Q${index + 1}`}</Box>
                                            <Box
                                                component={'span'}
                                                sx={{
                                                    color: 'red',
                                                    fontSize: '24px',
                                                    fontWeight: '600',
                                                    position: 'absolute',
                                                    top: '-7px',
                                                    right: '-17px',
                                                    display: `${question.required ? 'block' : 'none'}`,
                                                }}
                                            >
                                                *
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                fontWeight: '600',
                                                fontSize: '24px',
                                                wordBreak: 'break-word',
                                                color: 'rgba(0, 0, 0, 0.7)',
                                            }}
                                        >
                                            {question.question}
                                        </Box>
                                    </Box>
                                    <ExpandMoreIcon
                                        sx={{
                                            fontSize: '50px',
                                            color: '#8B8B8B',
                                        }}
                                    />
                                </Stack>
                            </>
                        ) : (
                            <>
                                <Stack
                                    direction={'row'}
                                    alignItems={'start'}
                                    gap={3}
                                    sx={{
                                        padding: '35px 15px 15px 35px',
                                        borderTopLeftRadius: '22px',
                                        borderTopRightRadius: '22px',
                                    }}
                                >
                                    <input
                                        {...register('id', {
                                            value: question.id,
                                        })}
                                        type="hidden"
                                    />
                                    <input
                                        {...register('questionNumber', {
                                            value: question.questionNumber,
                                        })}
                                        type="hidden"
                                    />
                                    <Box
                                        sx={{
                                            padding: '8px',
                                            textAlign: 'center',
                                            width: '65px',
                                            height: '65px',
                                            fontSize: '28px',
                                            borderRadius: '10px',
                                            background: '#FFFFFF',
                                            border: `1px solid ${index === selectedId ? colors.main : '#8B8B8B'}`,
                                            color:
                                                index === selectedId
                                                    ? colors.main
                                                    : common.black,
                                            fontWeight: '600',
                                            position: 'relative',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box>{`Q${index + 1}`}</Box>
                                        <Box
                                            component={'span'}
                                            sx={{
                                                color: 'red',
                                                fontSize: '24px',
                                                fontWeight: '600',
                                                position: 'absolute',
                                                top: '-7px',
                                                right: '-17px',
                                                display: `${question.required ? 'block' : 'none'}`,
                                            }}
                                        >
                                            *
                                        </Box>
                                    </Box>
                                    <BaseTextMetionsField
                                        name="question"
                                        placeholder="Nhập nội dung câu hỏi…"
                                        trigger={mentionTrigger}
                                        singleLine={false}
                                        maxLength={255}
                                        onAdd={(
                                            id,
                                            display,
                                            startPos,
                                            endPos
                                        ) => {
                                            reset(
                                                (prev) => ({
                                                    ...prev,
                                                    questionIdForTitle:
                                                        Number(id),
                                                    mentionStartPos: startPos,
                                                    mentionEndPos: endPos,
                                                    mentionValue: `\${${display}}`,
                                                }),
                                                { keepDirty: true }
                                            );
                                            if (id) {
                                                setMentionTrigger(
                                                    randomTrigger()
                                                );
                                            }
                                        }}
                                        displayTransform={(id, display) =>
                                            logicDisplayAnswerOnTitlteTransformMarkUp(
                                                display
                                            )
                                        }
                                        data={displayAnswerData}
                                        markup="${__display__}" // eslint-disable-line
                                        customMentionStyle={customMentionStyle}
                                    >
                                        <></>
                                    </BaseTextMetionsField>
                                    <BaseSelectField
                                        maxHeight={350}
                                        SelectProps={{
                                            renderValue: (value) => {
                                                const selectedOption =
                                                    questionTypeOptions.find(
                                                        (option) =>
                                                            option.value ===
                                                            value
                                                    );
                                                return selectedOption
                                                    ? selectedOption.label
                                                    : '';
                                            },
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                ...styleSelect[
                                                    '& .MuiInputBase-root'
                                                ],
                                                height: '65px',
                                                '& .MuiSelect-icon': {
                                                    right: '20px',
                                                    fontSize: '50px ',
                                                    color: 'primary.main',
                                                },
                                            },
                                            flex: 0.5,
                                        }}
                                        size="small"
                                        name={`questionType`}
                                    >
                                        {questionTypeOptions
                                            .filter((item) =>
                                                !isCampaignPublicWithCode
                                                    ? true
                                                    : [
                                                          'single_choice',
                                                          'emoji',
                                                          'score_star_full',
                                                          'score_star_half',
                                                          'score',
                                                      ].includes(item.value)
                                            )
                                            .map((item) => (
                                                <MenuItem
                                                    key={item.value}
                                                    value={item.value}
                                                    sx={{
                                                        ...styles.selectQuestionType,
                                                        '&.Mui-selected .icon':
                                                            {
                                                                display: 'none',
                                                            },
                                                        '&.Mui-selected .icon-selected':
                                                            {
                                                                display:
                                                                    'inline-flex',
                                                            },
                                                    }}
                                                >
                                                    <Box
                                                        className="icon"
                                                        sx={{
                                                            display:
                                                                'inline-flex',
                                                        }}
                                                    >
                                                        {item.icon}
                                                    </Box>
                                                    <Box
                                                        className="icon-selected"
                                                        sx={{
                                                            display: 'none',
                                                        }}
                                                    >
                                                        {item.iconSelected}
                                                    </Box>
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                    </BaseSelectField>
                                </Stack>
                                <Stack
                                    direction={'column'}
                                    alignItems={'baseline'}
                                    gap={3}
                                    sx={{
                                        padding: '0px 35px 20px 35px',
                                        borderTopLeftRadius: '22px',
                                        borderTopRightRadius: '22px',
                                    }}
                                >
                                    {question.skipLogic &&
                                        question.skipLogic.length > 0 && (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    alignItems={'center'}
                                                >
                                                    <Grid item xs={2}>
                                                        <Box className="display-logic-tag">
                                                            <Chip
                                                                label="Skip question"
                                                                color="error"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        {question.skipLogic.map(
                                                            (
                                                                skipLogic,
                                                                logicIndex
                                                            ) => (
                                                                <Box
                                                                    key={
                                                                        logicIndex
                                                                    }
                                                                    className="display-logic-text"
                                                                >
                                                                    {prepareSkipLogicText(
                                                                        skipLogic,
                                                                        logicIndex
                                                                    )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <IconButton
                                                            aria-label="delete"
                                                            color="error"
                                                            sx={{
                                                                padding: '0px',
                                                            }}
                                                            onClick={
                                                                onSetDeleteQuestionLogicSkip
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    {question.creeningLogic &&
                                        question.creeningLogic.length > 0 && (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    alignItems={'center'}
                                                >
                                                    <Grid item xs={2}>
                                                        <Box className="display-logic-tag">
                                                            <Chip
                                                                label="Creening logic"
                                                                color="info"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        {question.creeningLogic.map(
                                                            (
                                                                logic,
                                                                logicIndex
                                                            ) => (
                                                                <Box
                                                                    key={
                                                                        logicIndex
                                                                    }
                                                                    className="display-logic-text"
                                                                >
                                                                    {prepareCreeningLogicText(
                                                                        logic,
                                                                        logicIndex
                                                                    )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <IconButton
                                                            aria-label="delete"
                                                            color="error"
                                                            sx={{
                                                                padding: '0px',
                                                            }}
                                                            onClick={
                                                                onSetDeleteCreeningLogic
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    {question.branchLogic &&
                                        question.branchLogic.length > 0 && (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    alignItems={'center'}
                                                >
                                                    <Grid item xs={2}>
                                                        <Box className="display-logic-tag">
                                                            <Chip
                                                                label="Branch logic"
                                                                color="primary"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        {question.branchLogic.map(
                                                            (
                                                                logic,
                                                                logicIndex
                                                            ) => (
                                                                <Box
                                                                    key={
                                                                        logicIndex
                                                                    }
                                                                    className="display-logic-text"
                                                                >
                                                                    {prepareBranchLogicText(
                                                                        logic,
                                                                        logicIndex
                                                                    )}
                                                                </Box>
                                                            )
                                                        )}
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <IconButton
                                                            aria-label="delete"
                                                            color="error"
                                                            sx={{
                                                                padding: '0px',
                                                            }}
                                                            onClick={
                                                                onSetDeleteBranchLogic
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    {question.questionTitleLogic &&
                                        question.questionTitleLogic.id && (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    alignItems={'center'}
                                                >
                                                    <Grid item xs={2}>
                                                        <Box className="display-logic-tag">
                                                            <Chip
                                                                label="Display answer on title"
                                                                color="error"
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <Box className="display-logic-text">
                                                            {prepareQuestionTitleLogicText(
                                                                question.questionTitleLogic
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <IconButton
                                                            aria-label="delete"
                                                            color="error"
                                                            sx={{
                                                                padding: '0px',
                                                            }}
                                                            onClick={
                                                                onSetDeleteQuestionTitleLogic
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                    {question.answerFromOtherQuestionLogic &&
                                        question.answerFromOtherQuestionLogic
                                            .is_valid_logic &&
                                        question.answerFromOtherQuestionLogic
                                            .id && (
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <Grid
                                                    container
                                                    spacing={2}
                                                    alignItems={'center'}
                                                >
                                                    <Grid item xs={2}>
                                                        <Box className="display-logic-tag">
                                                            <Chip
                                                                label="Only display selected choice"
                                                                sx={{
                                                                    borderColor:
                                                                        (
                                                                            theme: Theme
                                                                        ) =>
                                                                            theme
                                                                                .palette
                                                                                .info
                                                                                .light,
                                                                    color: (
                                                                        theme: Theme
                                                                    ) =>
                                                                        theme
                                                                            .palette
                                                                            .info
                                                                            .light,
                                                                }}
                                                                variant="outlined"
                                                            />
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <Box className="display-logic-text">
                                                            {prepareAnswerFromOtherQuestionLogicText(
                                                                question.questionType,
                                                                question.answerFromOtherQuestionLogic
                                                            )}
                                                        </Box>
                                                    </Grid>
                                                    <Grid item xs="auto">
                                                        <IconButton
                                                            aria-label="delete"
                                                            color="error"
                                                            sx={{
                                                                padding: '0px',
                                                            }}
                                                            onClick={
                                                                onSetDeleteAnswerFromOtherQuestionLogic
                                                            }
                                                        >
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                </Stack>

                                {/* question type */}
                                <Stack>{renderQuestionType()}</Stack>
                            </>
                        )}
                    </Stack>
                    {selectedId === index ? (
                        <Stack
                            gap={'40px'}
                            sx={{
                                width: '56px',
                                padding: '10px',
                                borderRadius: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #0000004D',
                            }}
                        >
                            <IconButton
                                size="small"
                                aria-label="copy"
                                onClick={() => {
                                    if (!question.id || isDirty) {
                                        toast.error(
                                            'Hãy lưu câu hỏi trước khi sao chép'
                                        );
                                        return;
                                    }
                                    handleCopyQuestion &&
                                        handleCopyQuestion(question);
                                }}
                            >
                                <ContentCopyIcon sx={{ fontSize: 30 }} />
                            </IconButton>
                            {hasTwoOrMoreQuestions && (
                                <IconButton
                                    size="small"
                                    aria-label="delete"
                                    onClick={() => setIsDeleteDialogOpen(true)}
                                >
                                    <DeleteOutlineIcon sx={{ fontSize: 30 }} />
                                </IconButton>
                            )}
                            <IconButton
                                size="small"
                                aria-label="setting"
                                onClick={handleSelectLogicType}
                                sx={{
                                    display: isCampaignPublicWithCode
                                        ? 'none'
                                        : 'inline-flex',
                                    '& .MuiIconButton-root': {
                                        fill: 'red',
                                    },
                                }}
                            >
                                <SettingsOutlinedIcon sx={{ fontSize: 30 }} />
                            </IconButton>
                            {(question.skipLogic?.length ?? 0) === 0 && (
                                <IconButton
                                    sx={{
                                        display: isCampaignPublicWithCode
                                            ? 'none'
                                            : 'inline-flex',
                                    }}
                                    size="small"
                                    aria-label="bookmark"
                                    onClick={onFlipQuestionRequired}
                                >
                                    <StarBorderIcon sx={{ fontSize: 30 }} />
                                </IconButton>
                            )}
                        </Stack>
                    ) : (
                        <Stack
                            sx={{
                                width: '56px',
                            }}
                        />
                    )}
                </Stack>
            </FormProvider>
            {openModalLogic && (
                <ModalQuestionLogic
                    onClose={() => setOpenModalLogic(false)}
                    logicType={logicType}
                    open={openModalLogic}
                    question={question}
                    replaceQuestionByQuestionId={replaceQuestionByQuestionId}
                />
            )}
            {isDeleteDialogOpen && (
                <BaseDialog
                    open={isDeleteDialogOpen}
                    onClose={() => setIsDeleteDialogOpen(false)}
                    title="Xác nhận xóa câu hỏi"
                    fullWidth={false}
                    maxWidth={false}
                    sxContent={{
                        width: '978px',
                        padding: '45px 75px',
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            fontSize: '24px',
                            fontWeight: '400',
                        }}
                    >
                        Bạn có chắc muốn xóa câu hỏi
                        <b>{` Q${question.questionNumber}. ${question.question} `}</b>
                        này không?
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1,
                            mt: 5,
                        }}
                    >
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={styles.modalButton}
                            onClick={() => {
                                setIsDeleteDialogOpen(false);
                            }}
                        >
                            Đóng
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={styles.modalButton}
                            onClick={onConfirmDeleteQuestion}
                        >
                            Xoá
                        </Button>
                    </Box>
                </BaseDialog>
            )}
            <Menu
                id="demo-positioned-menu"
                aria-labelledby="demo-positioned-button"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClosePopoverLogicType}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                sx={{
                    transform: 'translate(-90px, 50px)',
                    '& .MuiPaper-root': {
                        borderRadius: '10px',
                        minWidth: '570px',
                    },
                }}
            >
                {renderLogicMenuItems()}
            </Menu>
            {isDeleteLogicDialogOpen && (
                <BaseDialog
                    open={isDeleteLogicDialogOpen}
                    onClose={() => setIsDeleteLogicDialogOpen(false)}
                    title="Xác nhận xóa cấu hình logic câu hỏi"
                    fullWidth={false}
                    maxWidth={false}
                    sxContent={{
                        width: '1100px',
                        padding: '45px 75px',
                        textAlign: 'center',
                    }}
                >
                    <Box
                        sx={{
                            fontSize: '24px',
                            fontWeight: '400',
                        }}
                    >
                        Bạn có chắc muốn xoá cấu hình logic câu hỏi
                        <b>{` Q${question.questionNumber}. ${question.question} `}</b>
                        này không?
                    </Box>
                    <Box
                        sx={{
                            fontSize: '24px',
                            fontWeight: '400',
                        }}
                    >
                        <b>Lưu ý: </b>
                        Khi xoá cấu hình logic câu hỏi này sẽ xoá vĩnh viễn tất
                        cả dữ liệu liên quan đến cấu hình này
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: 1,
                            mt: 5,
                        }}
                    >
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={styles.modalButton}
                            onClick={() => {
                                setIsDeleteLogicDialogOpen(false);
                            }}
                        >
                            Đóng
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            sx={styles.modalButton}
                            onClick={onConfirmDeleteQuestionLogic}
                        >
                            Xoá
                        </Button>
                    </Box>
                </BaseDialog>
            )}
        </>
    );
};
const styles = createStyleSheet({
    menuLogic: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        paddingLeft: '40px',
        '& span:last-of-type': {
            fontSize: '14px',
            color: '#737791',
        },
        '& span:first-of-type': {
            fontSize: '16px',
            color: common.black,
        },
    },
    modalButton: {
        textTransform: 'none',
        borderRadius: '60px',
        height: '60px',
        minWidth: '155px',
        fontSize: '20px',
        fontWeight: '700',
    },
    selectQuestionType: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        fontSize: '16px',
        backgroundColor: common.white,
        color: colors.main,
        '&.Mui-selected': {
            backgroundColor: colors.main,
            color: common.white,
            '&.icon-selected': {
                backgroundColor: 'unset',
            },
            '&:hover': {
                backgroundColor: colors.main,
                color: common.white,
            },
        },
        ':hover': {
            backgroundColor: 'var(--color-primary-light-20)',
            color: colors.main,
        },
    },
});
export default QuestionNewEditForm;
